import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { APP_CONFIG } from '@configuration/configuration';
import { ButtonColors } from '@models/enums/components/Button/ButtonColor.enum';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MapButtons } from '@models/types/components/MapButtons.type';
import { DvmService } from '@services/dvm.service';
import anime from 'animejs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-zoom-buttons',
    imports: [
        ButtonComponent
    ],
    templateUrl: './zoom-buttons.component.html',
    styleUrl: './zoom-buttons.component.css'
})
export class ZoomButtonsComponent implements AfterViewInit {

  // VIEW CHILDREN
  @ViewChild('zoomInButton', { read: ElementRef })
  public zoomInButton!:         ElementRef<HTMLElement>;

  @ViewChild('zoomOutButton', { read: ElementRef })
  public zoomOutButton!:        ElementRef<HTMLElement>;

  // SERVICES
  private readonly mapViewer:   MapViewerService      = inject(DvmService).viewer;
  private readonly view:        Signal<AppViews|null> = inject(DvmService).view;
  private readonly configuration: Signal<Configuration>  = inject(APP_CONFIG);
  private readonly mapButtons: Signal<MapButtons> = computed((): MapButtons=> this.configuration().Ui.MapButtons);
  private readonly destroyRef:  DestroyRef            = inject(DestroyRef);

  // STATE
  private         viewReady:      boolean              = false;
  protected readonly showButtons: WritableSignal<boolean> = signal(false);
  public readonly maxZoomState:   WritableSignal<boolean> = signal(false);
  public readonly minZoomState:   WritableSignal<boolean> = signal(false);
  protected readonly showZoomButtonElements = computed(() => this.mapButtons().zoom);

  // BUTTON STYLE
  public readonly type:         ButtonType            = ButtonType.Pill;
  public readonly color:        ButtonColors          = ButtonColors.Blur;


  // EFFECTS
  private readonly viewChange:  EffectRef             = effect((): void => {

    const view: AppViews | null = this.view();

    if(view === null) return;

    if(!this.viewReady) return;

    this.showElements(this.shouldShowZoomButtons(view));

  })

  // LIFECYCLE HOOKS
  ngAfterViewInit(): void {
    this.viewReady = true;
    this.initComponent();
  }

  // METHODS

  private updateZoomState(): void {
    if (!this.mapViewer.isInitialized()) {
      this.maxZoomState.set(false);
      this.minZoomState.set(false);
      return;
    }

    const isMaxZoom = this.mapViewer.scaling_factor === this.mapViewer.max_scaling_factor,
          isMinZoom = this.mapViewer.scaling_factor === this.mapViewer.min_scaling_factor;

    this.maxZoomState.set(isMaxZoom);
    this.minZoomState.set(isMinZoom);
  }

  private initComponent(): void {

    this.mapViewer.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {

      this.mapViewer.bindInterfaceAction(this.getNativeButton(this.zoomInButton.nativeElement), 'zoom-in');
      this.mapViewer.bindInterfaceAction(this.getNativeButton(this.zoomOutButton.nativeElement), 'zoom-out');

      this.mapViewer.getObservable('zooming').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.updateZoomState());

      this.updateZoomState();
      window.setTimeout((): void => this.showElements(this.shouldShowZoomButtons(this.view())), 500);

    })

  }

  private showElements(show: boolean): void {
    if(show === this.showButtons()) return;

    const elements: HTMLElement[] = [
      this.getNativeButton(this.zoomInButton.nativeElement),
      this.getNativeButton(this.zoomOutButton.nativeElement)
    ];

    anime.remove(elements);

    if(show) this.showButtons.set(true);

    anime({
      complete: (): void => {
        if(!show) this.showButtons.set(false);
      },
      targets:  elements,
      opacity:  show ? [0,1] : [1,0],
      easing:   'easeInOutQuad',
      duration: 1000,
      delay:    anime.stagger(200)
    })

  }

  private shouldShowZoomButtons(view: AppViews | null): boolean {
    return view !== null && view !== AppViews.Pano && this.showZoomButtonElements();
  }

  private getNativeButton(host: HTMLElement): HTMLButtonElement {
    return host.querySelector('button')!;
  }

}
